import { Injectable } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { environment } from 'environments/environment';

/** Milestone action names. docs/rum-events.md is their registry: change both together. */
export const RumActions = {
    QuoteStarted: 'quote_started',
    QuoteSubmitted: 'quote_submitted',
    InstallationConfirmed: 'installation_confirmed',
    ApplicationHandoff: 'application_handoff',
    DocumentsUploaded: 'documents_uploaded'
} as const;

/** Bump when a milestone's trigger or an attribute's meaning changes. */
export const MILESTONE_SCHEMA_VERSION = 1;

export type MilestoneOutcome = { outcome: 'success' } | { outcome: 'error'; statusCode?: number };

/** Partner-level RUM context. Never a person's identifier, name or email. */
export interface PartnerContext {
    partnerId: string;
    userClass?: string;
}

const PARTNER_CONTEXT_KEYS: Array<keyof PartnerContext> = ['partnerId', 'userClass'];

export type HandoffMethod = 'start' | 'continue' | 'apply_for_finance' | 'email_link' | 'sms_link';

/** The attributes each milestone may carry: IDs and enums only. */
export interface MilestoneContexts {
    [RumActions.QuoteStarted]: { flow: string };
    [RumActions.QuoteSubmitted]: { flow: string; quoteId?: string } & MilestoneOutcome;
    [RumActions.InstallationConfirmed]: {
        flow: string;
        quoteId: string;
        /** The step that failed, on paths that update the quote before confirming. */
        step?: 'update' | 'confirm';
    } & MilestoneOutcome;
    [RumActions.ApplicationHandoff]: {
        method: HandoffMethod;
        product: string;
        brokerId?: string;
        applicationId?: string;
        quoteId?: string;
    } & MilestoneOutcome;
    [RumActions.DocumentsUploaded]: {
        entry: 'supporting_documents' | 'condition';
        documentType: string;
        applicationId: string;
    } & MilestoneOutcome;
}

export type MilestoneAttributes<K extends keyof MilestoneContexts> = Omit<
    MilestoneContexts[K],
    'outcome' | 'statusCode'
>;

/**
 * `statusCode` is only present when the error carries one: AuthInterceptor rethrows the response body, not the
 * HttpErrorResponse.
 */
export function failedOutcome(error: unknown): MilestoneOutcome {
    const status = (error as { status?: unknown } | null)?.status;
    return typeof status === 'number' ? { outcome: 'error', statusCode: status } : { outcome: 'error' };
}

/**
 * Thin, injectable facade over the Datadog RUM SDK.
 *
 * The rest of the app depends on this service, NOT on `@datadog/browser-rum` directly,
 * so the vendor SDK has a single owner (this module) and instrumentation can be added
 * anywhere without repeating enable-guards. Every method no-ops when RUM is disabled
 * (dev / ci / mock, i.e. environment.useDatadogRum === false); consumers can replace the
 * whole service with a stub in their own tests.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryService {
    private get enabled(): boolean {
        return environment.useDatadogRum;
    }

    /** Report a handled error with optional domain context. */
    trackError(error: Error, context?: Record<string, unknown>): void {
        if (!this.enabled) {
            return;
        }
        datadogRum.addError(error, context);
    }

    /** Record a custom business action / event (e.g. 'quote_submitted'). */
    trackAction(name: string, context?: Record<string, unknown>): void {
        if (!this.enabled) {
            return;
        }
        datadogRum.addAction(name, context);
    }

    trackMilestone<K extends keyof MilestoneContexts>(name: K, context: MilestoneContexts[K]): void {
        this.trackAction(name, { ...(context as object), schemaVersion: MILESTONE_SCHEMA_VERSION });
    }

    /** Record a custom timing / journey milestone relative to the current view. */
    addTiming(name: string, time?: number): void {
        if (!this.enabled) {
            return;
        }
        datadogRum.addTiming(name, time);
    }

    /** In global context (`@context.partnerId`, `@context.userClass`), not `usr`: no per-broker identifier is sent. */
    setPartnerContext(context: PartnerContext): void {
        if (!this.enabled) {
            return;
        }
        PARTNER_CONTEXT_KEYS.forEach(key => datadogRum.setGlobalContextProperty(key, context[key]));
    }

    /** RUM keeps global context across page loads (`storeContextsAcrossPages`). */
    getPartnerContext(): PartnerContext | undefined {
        if (!this.enabled) {
            return undefined;
        }
        const { partnerId, userClass } = datadogRum.getGlobalContext();
        if (partnerId === undefined || partnerId === null) {
            return undefined;
        }
        return userClass === undefined
            ? { partnerId: String(partnerId) }
            : { partnerId: String(partnerId), userClass: String(userClass) };
    }

    clearPartnerContext(): void {
        if (!this.enabled) {
            return;
        }
        PARTNER_CONTEXT_KEYS.forEach(key => datadogRum.removeGlobalContextProperty(key));
    }

    /**
     * Ends the session only when RUM holds partner context, so with EnableRumUserContext off sign-outs don't split
     * it. Components call RumContextService.endUserSession, which also cancels a pending tag.
     */
    endUserSession(): void {
        // getPartnerContext() is undefined while RUM is disabled, so this also guards the SDK call.
        if (this.getPartnerContext() !== undefined) {
            this.clearPartnerContext();
            datadogRum.stopSession();
        }
    }
}
